import dayjs from 'dayjs';

export const savingsReportService = {
    // Báo cáo tổng quan
    generateOverviewReport(savings, transactions) {
        const currentDate = dayjs();

        // Thống kê cơ bản
        const totalSavings = savings.length;
        const activeSavings = savings.filter(s => s.trangThai === 'active').length;
        const maturedSavings = savings.filter(s =>
            dayjs(s.ngayDaoHan).isBefore(currentDate) && s.trangThai === 'active'
        ).length;
        const withdrawnSavings = savings.filter(s => s.trangThai === 'withdrawn').length;

        // Tính toán tài chính
        const totalPrincipal = savings.reduce((sum, s) => sum + s.soTienGoc, 0);
        const totalCurrentValue = savings.reduce((sum, s) => {
            if (s.trangThai === 'withdrawn') return sum;
            return sum + this.calculateCurrentValue(s).currentValue;
        }, 0);
        const totalInterestEarned = totalCurrentValue - totalPrincipal;

        // Hiệu suất
        const averageInterestRate = savings.length > 0 ?
            savings.reduce((sum, s) => sum + s.laiSuat, 0) / savings.length : 0;
        const effectiveYield = totalPrincipal > 0 ?
            (totalInterestEarned / totalPrincipal) * 100 : 0;

        return {
            totalSavings,
            activeSavings,
            maturedSavings,
            withdrawnSavings,
            totalPrincipal,
            totalCurrentValue,
            totalInterestEarned,
            averageInterestRate,
            effectiveYield,
            growthRate: this.calculateGrowthRate(savings)
        };
    },

    // Báo cáo theo ngân hàng
    generateBankReport(savings) {
        const bankStats = {};

        savings.forEach(saving => {
            const bank = saving.nganHang;
            if (!bankStats[bank]) {
                bankStats[bank] = {
                    count: 0,
                    totalPrincipal: 0,
                    totalCurrentValue: 0,
                    averageRate: 0,
                    activeSavings: 0
                };
            }

            bankStats[bank].count++;
            bankStats[bank].totalPrincipal += saving.soTienGoc;

            if (saving.trangThai === 'active') {
                bankStats[bank].activeSavings++;
                const current = this.calculateCurrentValue(saving);
                bankStats[bank].totalCurrentValue += current.currentValue;
            }
        });

        // Tính lãi suất trung bình cho mỗi ngân hàng
        Object.keys(bankStats).forEach(bank => {
            const bankSavings = savings.filter(s => s.nganHang === bank);
            bankStats[bank].averageRate = bankSavings.length > 0 ?
                bankSavings.reduce((sum, s) => sum + s.laiSuat, 0) / bankSavings.length : 0;
            bankStats[bank].totalInterest = bankStats[bank].totalCurrentValue - bankStats[bank].totalPrincipal;
        });

        return Object.entries(bankStats).map(([bank, stats]) => ({
            bank,
            ...stats
        })).sort((a, b) => b.totalCurrentValue - a.totalCurrentValue);
    },

    // Báo cáo theo kỳ hạn
    generateTermReport(savings) {
        const termStats = {};

        savings.forEach(saving => {
            const term = saving.kyHanText;
            if (!termStats[term]) {
                termStats[term] = {
                    count: 0,
                    totalPrincipal: 0,
                    totalCurrentValue: 0,
                    averageRate: 0,
                    activeSavings: 0
                };
            }

            termStats[term].count++;
            termStats[term].totalPrincipal += saving.soTienGoc;

            if (saving.trangThai === 'active') {
                termStats[term].activeSavings++;
                const current = this.calculateCurrentValue(saving);
                termStats[term].totalCurrentValue += current.currentValue;
            }
        });

        // Tính lãi suất trung bình cho mỗi kỳ hạn
        Object.keys(termStats).forEach(term => {
            const termSavings = savings.filter(s => s.kyHanText === term);
            termStats[term].averageRate = termSavings.length > 0 ?
                termSavings.reduce((sum, s) => sum + s.laiSuat, 0) / termSavings.length : 0;
            termStats[term].totalInterest = termStats[term].totalCurrentValue - termStats[term].totalPrincipal;
        });

        return Object.entries(termStats).map(([term, stats]) => ({
            term,
            ...stats
        })).sort((a, b) => b.totalCurrentValue - a.totalCurrentValue);
    },

    // Báo cáo hiệu suất theo thời gian
    generatePerformanceReport(savings, transactions) {
        const monthlyData = [];

        for (let i = 11; i >= 0; i--) {
            const month = dayjs().subtract(i, 'month');
            const monthStr = month.format('MM/YYYY');

            // Tiết kiệm mới trong tháng
            const newSavings = savings.filter(s =>
                dayjs(s.ngayTao).isSame(month, 'month')
            );

            // Giao dịch trong tháng
            const monthTransactions = transactions.filter(t =>
                dayjs(t.ngayGiaoDich).isSame(month, 'month')
            );

            const deposits = monthTransactions
                .filter(t => t.loaiGiaoDich === 'deposit')
                .reduce((sum, t) => sum + t.soTien, 0);

            const withdrawals = monthTransactions
                .filter(t => t.loaiGiaoDich === 'withdraw')
                .reduce((sum, t) => sum + t.soTien, 0);

            const newPrincipal = newSavings.reduce((sum, s) => sum + s.soTienGoc, 0);

            // Tính tổng giá trị tại thời điểm cuối tháng
            const endOfMonth = month.endOf('month');
            const totalValueAtMonth = savings
                .filter(s => dayjs(s.ngayTao).isBefore(endOfMonth))
                .reduce((sum, s) => {
                    if (s.trangThai === 'withdrawn') return sum;
                    const daysFromStart = endOfMonth.diff(dayjs(s.ngayTao), 'day');
                    const valueAtMonth = this.calculateValueAtTime(s, daysFromStart);
                    return sum + valueAtMonth;
                }, 0);

            monthlyData.push({
                month: monthStr,
                newSavingsCount: newSavings.length,
                newPrincipal,
                deposits,
                withdrawals,
                netFlow: newPrincipal + deposits - withdrawals,
                totalValue: totalValueAtMonth
            });
        }

        return monthlyData;
    },

    // Dự báo tăng trưởng
    generateGrowthForecast(savings) {
        const forecast = [];
        const currentDate = dayjs();

        for (let i = 1; i <= 12; i++) {
            const futureDate = currentDate.add(i, 'month');

            let totalForecastValue = 0;

            savings.forEach(saving => {
                if (saving.trangThai === 'withdrawn') return;

                const maturityDate = dayjs(saving.ngayDaoHan);
                if (futureDate.isAfter(maturityDate)) {
                    // Nếu đã đến hạn, giả sử tái tục với cùng lãi suất
                    const cyclesCompleted = Math.floor(futureDate.diff(maturityDate, 'month') / saving.kyHan) + 1;
                    const daysInCurrentCycle = futureDate.diff(
                        maturityDate.add((cyclesCompleted - 1) * saving.kyHan, 'month'),
                        'day'
                    );

                    const maturityValue = this.calculateCurrentValue(saving).currentValue;
                    const currentCycleValue = this.calculateValueFromPrincipal(
                        maturityValue, saving.laiSuat, daysInCurrentCycle, saving.loaiLai
                    );

                    totalForecastValue += currentCycleValue;
                } else {
                    // Chưa đến hạn, tính giá trị bình thường
                    const daysFromStart = futureDate.diff(dayjs(saving.ngayTao), 'day');
                    const forecastValue = this.calculateValueAtTime(saving, daysFromStart);
                    totalForecastValue += forecastValue;
                }
            });

            forecast.push({
                month: futureDate.format('MM/YYYY'),
                forecastValue: Math.round(totalForecastValue),
                growth: i === 1 ? 0 :
                    Math.round(((totalForecastValue - forecast[i - 2]?.forecastValue) / forecast[i - 2]?.forecastValue) * 100 * 100) / 100
            });
        }

        return forecast;
    },

    // Phân tích rủi ro và cơ hội
    generateRiskAnalysis(savings) {
        const analysis = {
            concentrationRisk: this.calculateConcentrationRisk(savings),
            liquidityRisk: this.calculateLiquidityRisk(savings),
            interestRateRisk: this.calculateInterestRateRisk(savings),
            opportunities: this.identifyOpportunities(savings)
        };

        return analysis;
    },

    // Helper methods
    calculateCurrentValue(saving) {
        const now = dayjs();
        const startDate = dayjs(saving.ngayTao);
        const daysPassed = now.diff(startDate, 'day');

        return this.calculateValueAtTime(saving, daysPassed);
    },

    calculateValueAtTime(saving, days) {
        const principal = saving.soTienGoc;
        const annualRate = saving.laiSuat / 100;
        const timeInYears = days / 365;

        let currentValue;

        switch (saving.loaiLai) {
            case 'simple':
                currentValue = principal * (1 + annualRate * timeInYears);
                break;
            case 'monthly':
                const monthlyRate = annualRate / 12;
                const months = days / 30.44;
                currentValue = principal * Math.pow(1 + monthlyRate, months);
                break;
            case 'quarterly':
                const quarterlyRate = annualRate / 4;
                const quarters = days / 91.25;
                currentValue = principal * Math.pow(1 + quarterlyRate, quarters);
                break;
            case 'yearly':
                currentValue = principal * Math.pow(1 + annualRate, timeInYears);
                break;
            default:
                currentValue = principal;
        }

        return Math.round(currentValue);
    },

    calculateValueFromPrincipal(principal, rate, days, compoundType) {
        const annualRate = rate / 100;
        const timeInYears = days / 365;

        let value;

        switch (compoundType) {
            case 'simple':
                value = principal * (1 + annualRate * timeInYears);
                break;
            case 'monthly':
                const monthlyRate = annualRate / 12;
                const months = days / 30.44;
                value = principal * Math.pow(1 + monthlyRate, months);
                break;
            case 'quarterly':
                const quarterlyRate = annualRate / 4;
                const quarters = days / 91.25;
                value = principal * Math.pow(1 + quarterlyRate, quarters);
                break;
            case 'yearly':
                value = principal * Math.pow(1 + annualRate, timeInYears);
                break;
            default:
                value = principal;
        }

        return Math.round(value);
    },

    calculateGrowthRate(savings) {
        if (savings.length < 2) return 0;

        const sortedSavings = savings.sort((a, b) =>
            dayjs(a.ngayTao).valueOf() - dayjs(b.ngayTao).valueOf()
        );

        const firstHalf = sortedSavings.slice(0, Math.floor(sortedSavings.length / 2));
        const secondHalf = sortedSavings.slice(Math.floor(sortedSavings.length / 2));

        const firstHalfTotal = firstHalf.reduce((sum, s) => sum + s.soTienGoc, 0);
        const secondHalfTotal = secondHalf.reduce((sum, s) => sum + s.soTienGoc, 0);

        return firstHalfTotal > 0 ?
            ((secondHalfTotal - firstHalfTotal) / firstHalfTotal) * 100 : 0;
    },

    calculateConcentrationRisk(savings) {
        const bankDistribution = this.generateBankReport(savings);
        const maxBankPercentage = Math.max(...bankDistribution.map(b =>
            (b.totalCurrentValue / bankDistribution.reduce((sum, bank) => sum + bank.totalCurrentValue, 0)) * 100
        ));

        return {
            level: maxBankPercentage > 70 ? 'high' : maxBankPercentage > 50 ? 'medium' : 'low',
            percentage: maxBankPercentage,
            recommendation: maxBankPercentage > 70 ?
                'Nên đa dạng hóa sang các ngân hàng khác' :
                'Mức độ tập trung hợp lý'
        };
    },

    calculateLiquidityRisk(savings) {
        const now = dayjs();
        const lockedSavings = savings.filter(s =>
            s.trangThai === 'active' && dayjs(s.ngayDaoHan).isAfter(now)
        );

        const totalLocked = lockedSavings.reduce((sum, s) =>
            sum + this.calculateCurrentValue(s).currentValue, 0
        );
        const totalValue = savings.reduce((sum, s) => {
            if (s.trangThai === 'withdrawn') return sum;
            return sum + this.calculateCurrentValue(s).currentValue;
        }, 0);

        const lockedPercentage = totalValue > 0 ? (totalLocked / totalValue) * 100 : 0;

        return {
            level: lockedPercentage > 80 ? 'high' : lockedPercentage > 60 ? 'medium' : 'low',
            percentage: lockedPercentage,
            recommendation: lockedPercentage > 80 ?
                'Nên có một số khoản tiết kiệm linh hoạt' :
                'Mức độ thanh khoản hợp lý'
        };
    },

    calculateInterestRateRisk(savings) {
        const rates = savings.map(s => s.laiSuat);
        const avgRate = rates.reduce((sum, rate) => sum + rate, 0) / rates.length;
        const variance = rates.reduce((sum, rate) => sum + Math.pow(rate - avgRate, 2), 0) / rates.length;
        const standardDeviation = Math.sqrt(variance);

        return {
            averageRate: avgRate,
            volatility: standardDeviation,
            level: standardDeviation > 2 ? 'high' : standardDeviation > 1 ? 'medium' : 'low',
            recommendation: standardDeviation > 2 ?
                'Lãi suất biến động lớn, cân nhắc cố định một phần' :
                'Mức độ rủi ro lãi suất chấp nhận được'
        };
    },

    identifyOpportunities(savings) {
        const opportunities = [];
        const now = dayjs();

        // Cơ hội tái tục
        const maturingSoon = savings.filter(s =>
            s.trangThai === 'active' &&
            dayjs(s.ngayDaoHan).diff(now, 'day') <= 30 &&
            dayjs(s.ngayDaoHan).diff(now, 'day') > 0
        );

        if (maturingSoon.length > 0) {
            opportunities.push({
                type: 'renewal',
                title: 'Cơ hội tái tục',
                description: `${maturingSoon.length} khoản sắp đến hạn, có thể tái tục với lãi suất tốt hơn`,
                priority: 'high'
            });
        }

        // Cơ hội đa dạng hóa
        const bankReport = this.generateBankReport(savings);
        if (bankReport.length === 1) {
            opportunities.push({
                type: 'diversification',
                title: 'Đa dạng hóa ngân hàng',
                description: 'Chỉ gửi ở 1 ngân hàng, nên xem xét đa dạng hóa',
                priority: 'medium'
            });
        }

        // Cơ hội tối ưu hóa lãi suất
        const lowRateSavings = savings.filter(s =>
            s.trangThai === 'active' && s.laiSuat < 5
        );

        if (lowRateSavings.length > 0) {
            opportunities.push({
                type: 'optimization',
                title: 'Tối ưu hóa lãi suất',
                description: `${lowRateSavings.length} khoản có lãi suất thấp (<5%), có thể tìm lãi suất tốt hơn`,
                priority: 'medium'
            });
        }

        return opportunities;
    }
};
